
  import { defineComponent, ref, onMounted, computed, watch, onUnmounted } from 'vue';
  import ProductCard from '@/components/Marketplace/ProductCard.vue';
  import MarketplaceService from '@/services/marketplace';
  import { useRoute, useRouter } from 'vue-router';
  import type { Product, Category } from '@/services/marketplace';
  import { useStore } from 'vuex';
  import productCategoriesService from '@/services/products';

  export default defineComponent({
    name: 'MarketplacePage',
    components: {
      ProductCard,
    },
    setup() {
      const selectedSort = ref('popularity');
      const selectedOrgTypes = ref<string[]>([]);
      const selectedRegion = ref('');
      const minPrice = ref(null);
      const maxPrice = ref(null);
      const appliedFilters = ref<string[]>([]);
      const searchQuery = ref('');

      const totalCount = ref(0);
      const pageSize = ref(20);
      const currentPage = computed(() => parseInt(route.query.page as string, 10) || 1);
      const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value));

      const isMobile = ref(window.innerWidth < 768);
      const updateWidth = () => {
        isMobile.value = window.innerWidth < 768;
      };

      const products = ref<Product[]>([]);
      const route = useRoute();
      const router = useRouter();
      const store = useStore();
      const categories = ref<Category[]>([]);
      const selectedCategories = ref<Category[]>([]);
      const isOpen = ref(false);
      const isClicked = ref(false);
      const hoveredCategory = ref<Category | null>(null);

      const isLoggedIn = computed(() => store.state.auth.status.loggedIn);
      const cartCount = computed(() => store.state.user.cartItems);

      const toggleDropdown = () => {
        isOpen.value = !isOpen.value;
      };

      const getCategoryName = (categoryId: string) => {
        const category = categories.value.find((cat) => cat.id === categoryId);
        return category ? category.name : 'Неизвестная категория';
      };

      const getCategories = () => {
        productCategoriesService.all().then((res) => {
          categories.value = res;
        });
      };

      // Логика фильтрации
      const filteredProducts = computed(() => {
        let filtered = products.value;

        // Фильтрация по стоимости
        if (minPrice.value !== null) {
          filtered = filtered.filter((product) => product.price >= (minPrice.value || 0));
        }
        if (maxPrice.value !== null) {
          filtered = filtered.filter((product) => product.price <= (maxPrice.value || 0));
        }

        if (selectedCategories.value.length) {
          const ids = new Set(selectedCategories.value.map((c) => c.id));
          filtered = filtered.filter((product) => ids.has(product.category?.id));
        }
        // Сортировка
        if (selectedSort.value === 'price-asc') {
          filtered.sort((a, b) => a.price - b.price);
        } else if (selectedSort.value === 'price-desc') {
          filtered.sort((a, b) => b.price - a.price);
        } else if (selectedSort.value === 'popularity') {
          filtered.sort((a, b) => b.rating - a.rating);
        }
        return filtered;
      });

      // Вычисляем ширину элемента в зависимости от длины текста
      const computedWidth = (category: Category) => {
        const baseWidth = 80; // Минимальная ширина в пикселях
        const charMultiplier = 6; // Ширина на 1 символ
        const maxWidth = 200; // Максимальная ширина

        const width = Math.min(baseWidth + category.name.length * charMultiplier, maxWidth);
        return `width: ${width}px`;
      };

      const getProducts = () => {
        const filters = {
          price_min: minPrice.value,
          price_max: maxPrice.value,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          categories: selectedCategories.value.map((c: any) => c.id),
          ordering:
            selectedSort.value === 'price-asc' ? 'price' : selectedSort.value === 'price-desc' ? '-price' : '-rating',
          search: searchQuery.value,
        };

        MarketplaceService.all(currentPage.value, filters).then((res) => {
          products.value = res.result;
          totalCount.value = res.count;
        });
      };

      const changePage = (page: number) => {
        if (page < 1 || page > totalPages.value) return;
        router.push({
          query: { ...route.query, page: page.toString() },
        });
      };

      watch(
        [minPrice, maxPrice, selectedCategories, selectedSort, searchQuery],
        () => {
          if (currentPage.value !== 1) {
            router.push({ query: { ...route.query, page: '1' } });
          } else {
            getProducts();
          }
        },
        { deep: true }
      );

      // При изменении страницы в URL — подгружаем данные
      watch(
        () => route.query.page,
        () => {
          getProducts();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      );

      const updateFilters = () => {
        appliedFilters.value = [];

        // Добавление выбранных форм организации
        if (selectedOrgTypes.value.length > 0) {
          appliedFilters.value.push(...selectedOrgTypes.value);
        }

        // Добавление диапазона цен
        if (minPrice.value !== null) {
          appliedFilters.value.push(`От ${minPrice.value}₽`);
        }
        if (maxPrice.value !== null) {
          appliedFilters.value.push(`До ${maxPrice.value}₽`);
        }
      };

      const removeFilter = (filter: string) => {
        if (selectedOrgTypes.value.includes(filter)) {
          selectedOrgTypes.value = selectedOrgTypes.value.filter((f) => f !== filter);
        } else if (selectedCategories.value.some((item) => item.id === filter)) {
          selectedCategories.value = selectedCategories.value.filter((category) => category.id !== filter);
        } else if (filter === selectedRegion.value) {
          selectedRegion.value = '';
        } else if (filter.startsWith('От')) {
          minPrice.value = null;
        } else if (filter.startsWith('До')) {
          maxPrice.value = null;
        }

        updateFilters();
      };
      const onSearchByRegions = () => {
        if (searchQuery.value) {
          store.dispatch('companies/searchProducts', searchQuery.value);
        }
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const preventNegativeInput = (event: any) => {
        if (event.key === '-' || event.key === 'e') {
          event.preventDefault();
        }
      };

      const toggleCategory = (category: Category) => {
        if (selectedCategories.value.some((c) => c.id === category.id)) return;
        selectedCategories.value.push(category);
        setTimeout(() => {
          const el = document.getElementById('products-list');
          if (el) {
            const offset = 120;
            const y = el.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }, 150);
      };

      const onMouseEnter = (category: Category) => {
        if (isMobile.value) return;
        hoveredCategory.value = category;
      };

      const onMouseLeave = () => {
        if (isMobile.value) return;
        hoveredCategory.value = null;
      };

      const handleParentClick = (category: Category) => {
        if (!isMobile.value) {
          toggleCategory(category);
          return;
        }

        // На Мобильных:
        if (category.childrens && category.childrens.length > 0) {
          if (hoveredCategory.value?.id === category.id) {
            toggleCategory(category);
            hoveredCategory.value = null;
          } else {
            hoveredCategory.value = category;
          }
        } else {
          toggleCategory(category);
        }
      };

      onMounted(() => {
        getProducts();
        getCategories();
        window.addEventListener('resize', updateWidth);

        if (isLoggedIn.value) {
          store.dispatch('user/getCartItems');
        }
      });

      onUnmounted(() => {
        window.removeEventListener('resize', updateWidth);
      });

      watch([selectedOrgTypes, selectedRegion, minPrice, maxPrice, selectedCategories], updateFilters, {
        immediate: true,
        deep: true,
      });

      watch(isLoggedIn, (val) => {
        if (val) {
          store.dispatch('user/getCartItems');
        }
      });

      return {
        searchQuery,
        appliedFilters,
        selectedSort,
        products,
        filteredProducts,
        minPrice,
        maxPrice,
        selectedCategories,
        categories,
        isOpen,
        isClicked,
        hoveredCategory,
        computedWidth,
        toggleDropdown,
        removeFilter,
        getProducts,
        onSearchByRegions,
        preventNegativeInput,
        getCategoryName,
        toggleCategory,
        totalCount,
        currentPage,
        totalPages,
        changePage,
        isMobile,
        isLoggedIn,
        cartCount,
        handleParentClick,
      };
    },
  });
